import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { useGetPromptsListQuery } from '../../../store/prompts/promptsApi';
import { usePrompts } from './PromptsContext';
import { getPromptId, getPromptStatusLabel } from './types';

type StatusFilter = 'active' | 'archived' | 'all';

const statusBadgeClass = (prompt: { archived?: boolean; published?: boolean; hasUnpublishedChanges?: boolean }) => {
    if (prompt.archived) {
        return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700';
    }
    if (prompt.published && prompt.hasUnpublishedChanges) {
        return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700';
    }
    if (prompt.published) {
        return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700';
    }
    return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
};

const PromptsListPage = () => {
    const navigate = useNavigate();
    const { canEdit } = usePrompts();
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');

    const { data, isLoading, isError, error } = useGetPromptsListQuery({
        page,
        size: 10,
        archived: statusFilter !== 'active',
    });

    const prompts = useMemo(() => {
        const elements = data?.elements ?? [];
        if (statusFilter === 'archived') {
            return elements.filter((p) => Boolean(p.archived));
        }
        if (statusFilter === 'active') {
            return elements.filter((p) => !p.archived);
        }
        return elements;
    }, [data?.elements, statusFilter]);
    const pageInfo = data?.page;

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return prompts;
        return prompts.filter((p) =>
            p.name.toLowerCase().includes(q) ||
            p.key.toLowerCase().includes(q) ||
            (p.description || '').toLowerCase().includes(q),
        );
    }, [prompts, search]);

    return (
        <div>
            <div className='flex flex-wrap items-start justify-between gap-4'>
                <div>
                    <h1 className='text-xl md:text-2xl font-bold text-[#101010] dark:text-primary-darkMode'>
                        Prompts
                    </h1>
                    <p className='mt-2 text-sm text-gray-600 dark:text-gray-400'>
                        Manage dynamic prompts, providers, and models.
                    </p>
                    {pageInfo && (
                        <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                            {pageInfo.totalElements} prompt{pageInfo.totalElements === 1 ? '' : 's'}
                        </p>
                    )}
                </div>
                <div className='flex flex-wrap items-center gap-3'>
                    {canEdit && (
                        <button
                            type='button'
                            onClick={() => navigate('/prompts/new')}
                            className='px-4 py-2 rounded-md text-sm text-white bg-primary-default'
                        >
                            Create prompt
                        </button>
                    )}
                </div>
            </div>

            <div className='mt-5 flex flex-wrap items-center gap-3'>
                <input
                    type='search'
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder='Search by name, key, or description...'
                    className='w-full max-w-md rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black px-3 py-2 text-sm dark:text-white'
                />
                <div className='flex rounded-md border border-gray-300 dark:border-gray-700 overflow-hidden'>
                    {([
                        { id: 'active', label: 'Active' },
                        { id: 'archived', label: 'Archived' },
                        { id: 'all', label: 'All' },
                    ] as const).map((tab) => (
                        <button
                            key={tab.id}
                            type='button'
                            onClick={() => {
                                setStatusFilter(tab.id);
                                setPage(1);
                            }}
                            className={`px-3 py-2 text-sm ${
                                statusFilter === tab.id
                                    ? 'bg-primary-default text-white'
                                    : 'bg-white dark:bg-black text-gray-700 dark:text-gray-300'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {isError && (
                <p className='mt-4 text-sm text-red-600 dark:text-red-400'>
                    Failed to load prompts. {(error as { data?: { message?: string } })?.data?.message || 'Please try again.'}
                </p>
            )}

            <div className='mt-5 overflow-x-auto border border-1 rounded-xl dark:border-borderColor-darkMode'>
                <table className='min-w-full bg-white dark:bg-black dark:text-white'>
                    <thead className='text-left text-sm font-medium text-black dark:text-white border-b'>
                        <tr>
                            <th className='px-4 py-3 min-w-[160px]'>Name</th>
                            <th className='px-4 py-3 min-w-[120px]'>Key</th>
                            <th className='px-4 py-3 min-w-[180px]'>Description</th>
                            <th className='px-4 py-3 min-w-[120px]'>Status</th>
                            <th className='px-4 py-3 min-w-[120px]'>Providers</th>
                            <th className='px-4 py-3 min-w-[120px]'>Models</th>
                            <th className='px-4 py-3 min-w-[80px]'>Version</th>
                            <th className='px-4 py-3 min-w-[140px]'>Updated</th>
                            <th className='px-4 py-3 min-w-[90px]'>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading &&
                            Array.from({ length: 5 }).map((_, i) => (
                                <tr key={`skeleton-${i}`} className='animate-pulse border-b'>
                                    {Array.from({ length: 9 }).map((__, j) => (
                                        <td key={`cell-${i}-${j}`} className='px-4 py-4'>
                                            <div className='h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4' />
                                        </td>
                                    ))}
                                </tr>
                            ))}

                        {!isLoading && filtered.length === 0 && (
                            <tr>
                                <td colSpan={9} className='px-4 py-8 text-center text-gray-500 dark:text-gray-400'>
                                    No prompts found
                                </td>
                            </tr>
                        )}

                        {!isLoading &&
                            filtered.map((prompt) => {
                                const id = getPromptId(prompt);
                                return (
                                    <tr
                                        key={id}
                                        className='hover:bg-gray-50 dark:hover:bg-bgColor-darkMode text-sm border-b'
                                    >
                                        <td className='px-4 py-4'>
                                            <div className='font-medium'>{prompt.name}</div>
                                            {prompt.hasUnpublishedChanges && (
                                                <span className='text-xs text-amber-600 dark:text-amber-400'>
                                                    Unpublished changes
                                                </span>
                                            )}
                                        </td>
                                        <td className='px-4 py-4 font-mono text-xs'>{prompt.key}</td>
                                        <td className='px-4 py-4 text-gray-600 dark:text-gray-400 line-clamp-2'>
                                            {prompt.description || '—'}
                                        </td>
                                        <td className='px-4 py-4'>
                                            <span
                                                className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(prompt)}`}
                                            >
                                                {getPromptStatusLabel(prompt)}
                                            </span>
                                        </td>
                                        <td className='px-4 py-4'>
                                            {(prompt.allowedProviders || []).join(', ') || '—'}
                                        </td>
                                        <td className='px-4 py-4'>
                                            {(prompt.allowedModels || []).join(', ') || '—'}
                                        </td>
                                        <td className='px-4 py-4'>v{prompt.version ?? '—'}</td>
                                        <td className='px-4 py-4'>
                                            {prompt.updatedAt
                                                ? moment(prompt.updatedAt).format('DD-MM-YYYY HH:mm')
                                                : '—'}
                                        </td>
                                        <td className='px-4 py-4'>
                                            <Link
                                                to={`/prompts/${id}`}
                                                className='text-primary dark:text-primary-darkMode hover:underline font-medium'
                                            >
												<img src="/assets/table-actions/see-more.svg" alt="see-more" className='w-4 h-4 rotate-90' />
                                            </Link>
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
            </div>

            {pageInfo && pageInfo.totalPages > 1 && (
                <div className='mt-4 flex items-center justify-end gap-3'>
                    <button
                        type='button'
                        disabled={page <= 1}
                        onClick={() => setPage((p) => p - 1)}
                        className='px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 text-sm disabled:opacity-50'
                    >
                        Previous
                    </button>
                    <span className='text-sm text-gray-600 dark:text-gray-400'>
                        Page {pageInfo.number} of {pageInfo.totalPages}
                    </span>
                    <button
                        type='button'
                        disabled={!pageInfo.hasNextPage}
                        onClick={() => setPage((p) => p + 1)}
                        className='px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 text-sm disabled:opacity-50'
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default PromptsListPage;
